import { createFileRoute, useParams, useNavigate } from '@tanstack/react-router'
import { assetPath } from '@/utils/assetPath'
import { useState, useEffect } from 'react'
import { useLook } from '@/hooks/api/useLooks'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useUpdateLook } from '@/hooks/api/useLooks'
import { useToast } from '@/components/shared/toast/Toast'
import { useImageUpload } from '@/hooks/useImageUpload'
import { ImageUploadType } from '@wtf/shared'

// Global UI Components
import { Button } from '@/components/shared/atoms/Button'
import Textarea from '@/components/shared/molecules/CustomTextarea'

// Global Icons
import ChecklistIcon from '@/icons/checklist'
import PencilIcon from '@/icons/pencil'
import ShareIcon from '@/icons/share'

// Look-specific components
import { LookHeader } from '@/components/looks/LookHeader'
import { ChooseFileBottomSheet } from '@/components/shared/bottom-sheets/ChooseFileBottomSheet'
import { ImageCropperBottomSheet } from '@/components/shared/bottom-sheets/ImageCropperBottomSheet'
import { CreateProductFlow } from '@/components/create/product/CreateProductFlow'
import { SelectNewOrExistingProductBottomSheet } from '@/components/create/product/SelectNewOrExistingProductBottomSheet'
import { SelectExistingProductsBottomSheet } from '@/components/products/bottom-sheets/SelectExistingProductsBottomSheet'
import { AddLookToCollectionBottomSheet } from '@/components/looks/bottom-sheets/AddLookToCollectionBottomSheet'
import { ProductGridCard } from '@/components/products/cards/ProductGridCard/ProductGridCard'
import { AddProductPlaceholder } from '@/components/products/placeholders/AddProductPlaceholder'
import { useBottomNav } from '@/contexts/BottomNavContext'
import { classNames } from '@/utils/classNames'

// Skeleton component for loading state
const Skeleton = () => {
    return (
        <div className="mt-2 animate-pulse px-2">
            <div className="h-120 w-full shrink-0 rounded-2xl bg-gray-300"></div>
            <div className="mt-4 flex-1">
                <div className="mb-2 h-12 w-full rounded-2xl bg-gray-300"></div>
                <div className="mt-3 mb-2 h-20 w-full rounded-2xl bg-gray-300"></div>
            </div>

            <div className="grid grid-cols-2 gap-3">
                {Array.from({ length: 5 }).map((_, index) => (
                    <div
                        key={index}
                        className="h-60 w-full rounded-2xl bg-gray-300"
                    ></div>
                ))}
            </div>
        </div>
    )
}

export const Route = createFileRoute('/looks_/$id/edit')({
    component: EditLookRoute,
})

function EditLookRoute() {
    const { id: lookId } = useParams({ from: '/looks_/$id/edit' })
    const navigate = useNavigate()
    const { data: look, isLoading } = useLook(lookId)
    const { user } = useCurrentUser()
    const updateLook = useUpdateLook()
    const { isBottomNavVisible, bottomNavHeight } = useBottomNav()
    const { addToast } = useToast()

    // Look cover upload with enhanced hook - replaces ~140 lines with 7 lines!
    const coverUpload = useImageUpload({
        type: ImageUploadType.LOOK_COVER,
        initialPreview: look?.imageUrl || undefined,
        onSuccess: () => addToast('Cover uploaded successfully', 'success'),
        onError: (error) => addToast(error, 'error'),
    })

    // Local form state
    const [description, setDescription] = useState(look?.description ?? '')
    const [products, setProducts] = useState<string[]>(look?.productIds ?? [])
    // Initialize local state when look loads
    useEffect(() => {
        if (look) {
            setDescription(look.description)
            setProducts(look.productIds)
        }
    }, [look])

    // Bottom-sheet states
    const [showSelectNewOrExistingProduct, setShowSelectNewOrExistingProduct] =
        useState(false)
    const [showSelectExistingProducts, setShowSelectExistingProducts] =
        useState(false)
    const [createNewProductFlowOpen, setCreateNewProductFlowOpen] =
        useState(false)
    const [addLookToCollectionOpen, setAddLookToCollectionOpen] =
        useState(false)

    // Redirect non-owners
    useEffect(() => {
        if (look && user && look.ownerId !== user.uid) {
            navigate({ to: `/looks/${lookId}` })
        }
    }, [look, user, navigate, lookId])

    // Handlers to manipulate local state
    const handleImageUploadProduct = () => {
        setShowSelectNewOrExistingProduct(true)
    }

    const handleRemoveProduct = (productId: string) => {
        setProducts((prev) => prev.filter((id) => id !== productId))
    }

    const handleExistingProductsSelected = (
        selected: string[],
        removed: string[] = []
    ) => {
        setProducts((prev) => {
            const kept = prev.filter((id) => !removed.includes(id))
            const merged = Array.from(new Set([...kept, ...selected]))
            return merged
        })
    }

    // Handle transiently-created products in look-edit (not persisted until save)
    const handleNewProductCreated = (newId: string) => {
        setProducts((prev) => [...prev, newId])
        setCreateNewProductFlowOpen(false)
    }

    // Submit handler
    const handleSubmit = async () => {
        await updateLook.mutateAsync({
            id: lookId!,
            data: {
                description,
                imageUrl: coverUpload.previewUrl,
                products: products.map((pid) => ({ productId: pid })),
            },
        })
        navigate({ to: `/looks/${lookId}`, replace: true })
        // setAddLookToCollectionOpen(true);
    }

    if (isLoading) {
        return <Skeleton />
    }
    if (!look) {
        return (
            <div className="mx-auto max-w-lg py-20 text-center">
                Look not found.
            </div>
        )
    }

    return (
        <div
            className={classNames(
                'flex min-h-dvh w-full max-w-lg flex-col overflow-hidden bg-white pt-1 lg:mx-auto',
                isBottomNavVisible ? `mb-${bottomNavHeight}` : undefined
            )}
        >
            <div className="relative">
                <LookHeader
                    showActionLeft
                    // showCloseIcon={false}
                    showActionRight={false}
                    image={coverUpload.previewUrl}
                    showProducts={false}
                    onReport={() => { }}
                />
                {coverUpload.isUploading && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/20">
                        <div className="h-8 w-8 animate-spin rounded-full border-4 border-white border-t-transparent"></div>
                    </div>
                )}
            </div>

            {/* Cover upload error message */}
            {coverUpload.uploadError && (
                <div className="mx-2 mt-2 flex items-center rounded-lg border border-red-200 bg-red-50 px-3 py-2">
                    <img
                        src={assetPath('icons/status/error.svg')}
                        alt="Error"
                        className="mr-2 h-4 w-4"
                    />
                    <span className="text-sm text-red-600">
                        {coverUpload.uploadError}
                    </span>
                    <button
                        onClick={coverUpload.clearError}
                        className="ml-2 text-red-600 hover:text-red-800"
                    >
                        ✕
                    </button>
                </div>
            )}

            <form
                onSubmit={(e) => {
                    e.preventDefault()
                    handleSubmit()
                }}
            >
                <section className="mt-4 border-b border-[#f0f0f0] px-2 pb-4">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={coverUpload.triggerUpload}
                        className="flex w-full items-center justify-center gap-2 border-[1.5px] border-black py-3.5 text-sm font-medium"
                        disabled={coverUpload.isUploading}
                    >
                        {coverUpload.isUploading ? (
                            'Uploading...'
                        ) : coverUpload.previewUrl ? (
                            <>
                                <PencilIcon
                                    stroke="black"
                                    className="text-xl"
                                />
                                Edit Cover
                            </>
                        ) : (
                            <>
                                <ShareIcon width={20} height={20} />
                                Upload cover
                            </>
                        )}
                    </Button>
                    {/* Cover image errors handled externally if needed */}

                    {/* Description Field */}
                    <div className="mt-4">
                        <Textarea
                            showCounter={false}
                            label="Description"
                            containerClassName=""
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                        />
                    </div>
                </section>

                {/* Products Grid */}
                <section className="mt-4 mb-30 px-2">
                    <div className="grid grid-cols-2 gap-x-2.5 gap-y-2">
                        <AddProductPlaceholder onClick={handleImageUploadProduct} />
                        {products.map((pid) => (
                            <ProductGridCard
                                key={pid}
                                editMode
                                productId={pid}
                                onRemove={() => handleRemoveProduct(pid)}
                                onClick={() => { }}
                            />
                        ))}
                    </div>
                </section>

                {/* Save Button */}
                <div
                    className={classNames(
                        'fixed inset-x-0 z-30 mx-auto w-full max-w-md bg-white/95 px-2 pb-2',
                        isBottomNavVisible
                            ? `bottom-${bottomNavHeight}`
                            : 'bottom-0'
                    )}
                >
                    <Button
                        type="button"
                        fullWidth
                        disabled={
                            updateLook.isPending ||
                            coverUpload.isUploading ||
                            !!coverUpload.uploadError
                        }
                        onClick={handleSubmit}
                        className="flex w-full items-center justify-center gap-2 text-sm font-medium"
                    >
                        <ChecklistIcon className="text-xl" stroke="white" />
                        {coverUpload.uploadError
                            ? 'Fix cover upload error'
                            : coverUpload.isUploading
                                ? 'Uploading cover...'
                                : updateLook.isPending
                                    ? 'Saving...'
                                    : 'Save'}
                    </Button>
                </div>
            </form>

            {/* Bottom Sheets */}
            <section className="fixed">
                <ChooseFileBottomSheet {...coverUpload.filePickerProps} />
                <ImageCropperBottomSheet {...coverUpload.cropperProps} />
                <SelectNewOrExistingProductBottomSheet
                    open={showSelectNewOrExistingProduct}
                    onClose={() => setShowSelectNewOrExistingProduct(false)}
                    onMyProductSelect={() => {
                        setShowSelectExistingProducts(true)
                        setShowSelectNewOrExistingProduct(false)
                    }}
                    onNewProductSelect={() => {
                        setCreateNewProductFlowOpen(true)
                        setShowSelectNewOrExistingProduct(false)
                    }}
                />
                {createNewProductFlowOpen && (
                    <CreateProductFlow
                        hideAddToCollection={true}
                        onClose={() => setCreateNewProductFlowOpen(false)}
                        onProductCreated={handleNewProductCreated}
                    />
                )}
                <SelectExistingProductsBottomSheet
                    open={showSelectExistingProducts}
                    onClose={() => setShowSelectExistingProducts(false)}
                    onProductsSelected={handleExistingProductsSelected}
                    initialSelectedProducts={products}
                />
                <AddLookToCollectionBottomSheet
                    open={addLookToCollectionOpen}
                    onClose={() => setAddLookToCollectionOpen(false)}
                    onNewLookClose={() => setCreateNewProductFlowOpen(false)}
                    lookId={lookId!}
                />
            </section>
        </div>
    )
}
